import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchWithAuth, getSites } from '../api/backendApi';
import { useAuth } from './AuthContext';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';
import {
  readSupportedTenantSession,
  subscribeSupportedTenantSession,
} from '../pages/Admin/supportedTenantsSession';

const IncidentContext = createContext();

export const useIncident = () => {
  const context = useContext(IncidentContext);
  if (!context) {
    throw new Error('useIncident must be used within IncidentProvider');
  }
  return context;
};

export const IncidentProvider = ({ children }) => {
  const { currentUser, userData } = useAuth();
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activeIncident, setActiveIncident] = useState(null);
  const [forcedIncident, setForcedIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionLost, setConnectionLost] = useState(false);
  const [siteMap, setSiteMap] = useState({}); // Phase 1B: Site name mapping

  const didRestoreLastIncidentRef = useRef(false);

  // Get user roles
  const rolesKey = useMemo(() => {
    if (Array.isArray(userData?.roles)) return userData.roles.join('|');
    return '';
  }, [userData?.roles]);

  const userRoles = useMemo(() => {
    const roles = Array.isArray(userData?.roles) ? [...userData.roles] : [];
    const singleRole = userData?.role;
    if (singleRole && !roles.includes(singleRole)) roles.push(singleRole);
    return roles;
  }, [rolesKey, userData?.role]);

  const userRolesDepKey = useMemo(() => {
    return userRoles.slice().sort().join('|');
  }, [userRoles]);

  const userId = currentUser?.uid;
  const [supportTenantId, setSupportTenantId] = useState(() => readSupportedTenantSession()?.tenantId || '');
  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportTenantId(readSupportedTenantSession()?.tenantId || '');
  }), []);
  const tenantId = supportTenantId || userData?.tenantId;
  const supportReads = Boolean(supportTenantId);

  const activeIncidentIdsKey = useMemo(() => {
    if (!Array.isArray(activeIncidents) || activeIncidents.length === 0) return '';
    return activeIncidents
      .map((i) => i?.incidentId)
      .filter(Boolean)
      .slice()
      .sort()
      .join('|');
  }, [activeIncidents]);

  // Cooldown per incident assignment fetch to avoid spamming the same failing incident
  const incidentAssignmentsCooldownRef = useRef(new Map()); // incidentId -> retryAtMs
  // Global backoff if assignment polling is consistently failing
  const assignmentPollDisabledUntilRef = useRef(0);
  const assignmentPollFailureStreakRef = useRef(0);

  // Forced incident lifecycle tracking
  // Helps prevent forced incident from being cleared immediately if active-incidents polling hasn't caught up yet.
  const forcedIncidentLifecycleRef = useRef({
    incidentId: null,
    setAtMs: 0,
    observedInActiveList: false,
    missingSinceMs: 0,
  });

  // Phase 1B: Real-time subscription to sites collection (eliminates N+1 reads)
  useEffect(() => {
    if (!tenantId || supportReads) return undefined;

    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', tenantId, 'sites');
    const sitesQuery = query(sitesRef);

    if (import.meta.env?.DEV) {
      console.log('[INCIDENT] Setting up real-time sites subscription...');
    }
    const unsubscribe = onSnapshot(
      sitesQuery,
      (snapshot) => {
        const sitesMap = {};
        snapshot.docs.forEach((doc) => {
          sitesMap[doc.id] = doc.data().name || doc.id;
        });
        if (import.meta.env?.DEV) {
          console.log(`[INCIDENT] Sites loaded: ${Object.keys(sitesMap).length} sites`);
        }
        setSiteMap(sitesMap);
      },
      (error) => {
        console.error('[INCIDENT] Error loading sites:', error);
      }
    );

    return () => unsubscribe();
  }, [supportReads, tenantId]);

  useEffect(() => {
    if (!userId || !tenantId || !supportReads) return undefined;
    let cancelled = false;
    const loadSites = async () => {
      try {
        const response = await getSites(tenantId);
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (cancelled) return;
        const sitesMap = {};
        rows.forEach((site) => {
          const id = site?.id || site?.siteId;
          if (id) sitesMap[id] = site?.name || id;
        });
        setSiteMap(sitesMap);
      } catch (error) {
        if (!cancelled) console.error('[INCIDENT] Error loading sites:', error);
      }
    };
    loadSites();
    const timer = setInterval(loadSites, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [supportReads, tenantId, userId]);

  // Real-time subscription to active incidents via Firestore (replaces 5s HTTP poll)
  useEffect(() => {
    if (!tenantId || supportReads) {
      if (!supportReads && !tenantId) setLoading(false);
      return undefined;
    }

    const db = getFirestore();
    const q = query(
      collection(db, 'tenants', tenantId, 'incidents'),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const incidents = snapshot.docs.map((doc) => ({ incidentId: doc.id, ...doc.data() }));
        setActiveIncidents(incidents);
        setConnectionLost(false);
        setLoading(false);
      },
      (error) => {
        console.error('[INCIDENT] Error in incidents snapshot:', error);
        setConnectionLost(true);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [supportReads, tenantId]);

  useEffect(() => {
    if (!userId || !tenantId || !supportReads) return undefined;
    let cancelled = false;
    const loadIncidents = async () => {
      try {
        const response = await fetchWithAuth(
          `/api/v2/tenants/${encodeURIComponent(tenantId)}/incidents?status=active`,
          'GET',
          null,
          { suppressErrorLog: true }
        );
        const incidents = response?.data?.incidents || response?.incidents || [];
        if (cancelled) return;
        setActiveIncidents(Array.isArray(incidents) ? incidents : []);
        setConnectionLost(false);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('[INCIDENT] Error in incidents snapshot:', error);
        setConnectionLost(true);
        setLoading(false);
      }
    };
    loadIncidents();
    const timer = setInterval(loadIncidents, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [supportReads, tenantId, userId]);

  // Poll assignments for current user using backend API
  useEffect(() => {
    if (!tenantId || !userId || userRoles.length === 0 || activeIncidents.length === 0) {
      return;
    }

    if (import.meta.env?.DEV) {
      console.log('[INCIDENT] Polling assignments for user:', userId, 'roles:', userRoles);
    }

    const loadAssignments = async () => {
      try {
        const now = Date.now();
        if (now < assignmentPollDisabledUntilRef.current) {
          return;
        }

        const allAssignments = [];
        let anySuccess = false;
        let anyFailure = false;

        // Load assignments for each active incident
        for (const incident of activeIncidents) {
          const incidentId = incident?.incidentId;
          if (!incidentId) continue;

          const retryAt = incidentAssignmentsCooldownRef.current.get(incidentId);
          if (retryAt && retryAt > now) {
            continue;
          }

          try {
            const response = await fetchWithAuth(
              `/api/v2/tenants/${tenantId}/incidents/${incidentId}/assignments`,
              'GET',
              null,
              { suppressErrorLog: true }
            );

            const nextAssignments = response?.data?.assignments || response?.assignments;
            if (Array.isArray(nextAssignments)) {
              anySuccess = true;
              incidentAssignmentsCooldownRef.current.delete(incidentId);

              // Filter assignments relevant to this user
              const relevantAssignments = nextAssignments.filter((assignment) => {
                if (!assignment.active) return false;

                // Check role-based assignments
                if (assignment.target?.kind === 'role' && userRoles.includes(assignment.target.value)) {
                  return true;
                }

                // Check user-specific assignments
                if (assignment.target?.kind === 'user' && assignment.target.value === userId) {
                  return true;
                }

                return false;
              });

              allAssignments.push(...relevantAssignments);
            }
          } catch (e) {
            anyFailure = true;
            // Cooldown this incident for 60s to avoid repeated 500s for a single incident
            incidentAssignmentsCooldownRef.current.set(incidentId, now + 60_000);

            if (import.meta.env?.DEV) {
              console.warn('[INCIDENT] Assignment fetch failed (cooling down incident)', {
                tenantId,
                incidentId,
                error: e?.message,
              });
            }

            // Continue loading other incidents
          }
        }

        // Update global backoff state
        if (anySuccess) {
          assignmentPollFailureStreakRef.current = 0;
          assignmentPollDisabledUntilRef.current = 0;
        } else if (anyFailure) {
          assignmentPollFailureStreakRef.current += 1;
          const backoffMs = Math.min(60_000, 5_000 * (2 ** Math.min(6, assignmentPollFailureStreakRef.current - 1)));
          assignmentPollDisabledUntilRef.current = Date.now() + backoffMs;
        }

        if (import.meta.env?.DEV) {
          console.log('[INCIDENT] Assignments updated:', allAssignments.length);
        }
        setAssignments(allAssignments);
        setConnectionLost(!anySuccess && anyFailure);
      } catch (error) {
        console.error('[INCIDENT] Error loading assignments:', error);
        setConnectionLost(true);
      }
    };

    // Initial load
    loadAssignments();

    // Poll every 30 seconds for updates
    const interval = setInterval(loadAssignments, 30000);

    return () => clearInterval(interval);
  }, [tenantId, userId, userRolesDepKey, activeIncidentIdsKey]);

  const clearForcedIncident = useCallback((incidentId = null) => {
    setForcedIncident((curr) => {
      if (!incidentId) return null;
      if (curr?.incidentId === incidentId) return null;
      return curr;
    });
    setActiveIncident((curr) => {
      if (!incidentId) return null;
      if (curr?.incidentId === incidentId) return null;
      return curr;
    });
  }, []);

  // Track/clear forced incident safely.
  // We only clear after:
  // - the incident has been observed in the active list at least once AND then disappears for a short window, OR
  // - a grace period passes without ever observing it in the active list (fallback).
  useEffect(() => {
    const forcedIncidentId = forcedIncident?.incidentId;

    if (!forcedIncidentId) {
      forcedIncidentLifecycleRef.current = {
        incidentId: null,
        setAtMs: 0,
        observedInActiveList: false,
        missingSinceMs: 0,
      };
      return;
    }

    const now = Date.now();
    const lifecycle = forcedIncidentLifecycleRef.current;

    if (lifecycle.incidentId !== forcedIncidentId) {
      forcedIncidentLifecycleRef.current = {
        incidentId: forcedIncidentId,
        setAtMs: now,
        observedInActiveList: false,
        missingSinceMs: 0,
      };
    }

    const isInActiveList = activeIncidents.some(
      (i) => i?.incidentId === forcedIncidentId && i?.status === 'active'
    );

    if (isInActiveList) {
      forcedIncidentLifecycleRef.current.observedInActiveList = true;
      forcedIncidentLifecycleRef.current.missingSinceMs = 0;
      return;
    }

    // Not in active list
    const updated = forcedIncidentLifecycleRef.current;
    if (!updated.observedInActiveList) {
      // Give polling time to catch up after a forced-open.
      const graceMs = 60_000;
      if (now - updated.setAtMs < graceMs) return;

      // If it still never showed up as active after the grace window, clear it.
      setForcedIncident(null);
      return;
    }

    // It was observed active at least once; clear after it has been missing for a short window.
    if (!updated.missingSinceMs) {
      updated.missingSinceMs = now;
      return;
    }

    const missingMs = now - updated.missingSinceMs;
    if (missingMs > 10_000) {
      setForcedIncident(null);
    }
  }, [forcedIncident, activeIncidentIdsKey]);

  // Determine active incident (highest priority assignment)
  useEffect(() => {
    if (forcedIncident && forcedIncident.status === 'active') {
      setActiveIncident(forcedIncident);
      return;
    }

    if (assignments.length === 0 || activeIncidents.length === 0) {
      setActiveIncident(null);
      return;
    }

    // Find the incident with highest priority assignment
    let highestPriorityAssignment = null;
    let highestPriority = -Infinity;

    assignments.forEach(assignment => {
      if (assignment?.autoOpen === false) return;
      const incident = activeIncidents.find(i => i.incidentId === assignment.incidentId);
      if (incident && assignment.priority > highestPriority) {
        highestPriority = assignment.priority;
        highestPriorityAssignment = { ...assignment, incident };
      }
    });

    if (highestPriorityAssignment) {
      if (import.meta.env?.DEV) {
        console.log('[INCIDENT] Setting active incident:', highestPriorityAssignment.incident.incidentId);
      }
      setActiveIncident(highestPriorityAssignment.incident);
    } else {
      setActiveIncident(null);
    }
  }, [assignments, activeIncidents, forcedIncident]);

  const openIncidentById = useCallback(async (incidentId) => {
    if (!tenantId || !incidentId) return null;

    try {
      const response = await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/incidents/${incidentId}`,
        'GET',
        null,
        { suppressErrorLog: true }
      );

      const incident = response?.data || response?.incident;
      if (incident && incident.status === 'active') {
        setForcedIncident(incident);
        setActiveIncident(incident);
        return incident;
      }
      return null;
    } catch (e) {
      console.error('[INCIDENT] Failed to open incident by id', { incidentId, error: e });
      return null;
    }
  }, [tenantId]);

  useEffect(() => {
    if (didRestoreLastIncidentRef.current) return;
    if (!tenantId || !userId) return;
    didRestoreLastIncidentRef.current = true;

    try {
      const key = `incident:lastOpen:${tenantId}:${userId}`;
      const lastIncidentId = String(localStorage.getItem(key) || '').trim();
      if (!lastIncidentId) return;
      openIncidentById(lastIncidentId).then((incident) => {
        if (!incident) {
          try {
            localStorage.removeItem(key);
          } catch (_) {
            // ignore
          }
        }
      });
    } catch (_) {
      // ignore
    }
  }, [tenantId, userId, openIncidentById]);

  useEffect(() => {
    if (!tenantId || !userId) return;
    const key = `incident:lastOpen:${tenantId}:${userId}`;
    try {
      if (activeIncident?.incidentId && activeIncident?.status === 'active') {
        localStorage.setItem(key, activeIncident.incidentId);
      } else {
        localStorage.removeItem(key);
      }
    } catch (_) {
      // ignore
    }
  }, [tenantId, userId, activeIncident?.incidentId, activeIncident?.status]);

  // Modal open state (derived from activeIncident)
  const modalOpen = activeIncident !== null && activeIncident.status === 'active';

  const value = {
    activeIncidents,
    assignments,
    activeIncident,
    modalOpen,
    loading,
    connectionLost,
    userRoles,
    tenantId,
    userId,
    openIncidentById,
    clearForcedIncident,
    siteMap // Phase 1B: Expose siteMap for incident site display
  };

  return (
    <IncidentContext.Provider value={value}>
      {children}
    </IncidentContext.Provider>
  );
};
